import { COMPANY, forumText, PRODUCT } from './legal.config';

/*
 * Texto-base dos documentos legais, como dados tipados (a página só renderiza).
 * RASCUNHO para validação jurídica. Descreve apenas o que o aplicativo faz hoje.
 * Espelho em Markdown: docs/juridico/ (manter os dois em sincronia).
 */

export type LegalBlock = { kind: 'p'; text: string } | { kind: 'ul'; items: readonly string[] } | { kind: 'h3'; text: string };

export interface LegalSection {
  id: string;
  title: string;
  blocks: readonly LegalBlock[];
}

export interface LegalDoc {
  title: string;
  intro: string;
  sections: readonly LegalSection[];
}

const C = COMPANY.name;
const P = PRODUCT.name;
const N = PRODUCT.customerNoun;
const MAIL = COMPANY.privacyEmail;

const p = (text: string): LegalBlock => ({ kind: 'p', text });
const ul = (...items: string[]): LegalBlock => ({ kind: 'ul', items });
const h3 = (text: string): LegalBlock => ({ kind: 'h3', text });

export const PRIVACY: LegalDoc = {
  title: 'Política de Privacidade',
  intro: `Esta política explica como o ${P}, serviço de gestão para barbearias da ${C}, trata dados pessoais, em conformidade com a Lei Geral de Proteção de Dados (Lei 13.709/2018, LGPD).`,
  sections: [
    {
      id: 'quem-somos',
      title: '1. Quem somos e nossos papéis',
      blocks: [
        p(`O ${P} é oferecido pela ${C}. Para a LGPD, atuamos em dois papéis diferentes, conforme o dado:`),
        ul(
          `Controladora: dos dados da conta de quem usa o sistema (nome, e-mail, senha), da assinatura e cobrança do ${N}, e dos registros de uso e segurança do sistema. Nesses casos, decidimos como e por que os dados são tratados.`,
          `Operadora: dos dados que a ${N} cadastra sobre seus próprios clientes (agenda, cadastro, pagamentos, fidelidade, mensagens). Nesse caso, a ${N} é a controladora e nós tratamos os dados em nome dela e conforme as instruções dela, para prestar o serviço contratado.`,
        ),
        p(`Fale conosco em ${MAIL}. Esse mesmo endereço atende como contato geral e como canal do encarregado pelo tratamento de dados pessoais.`),
      ],
    },
    {
      id: 'dados-tratados',
      title: '2. Dados que tratamos',
      blocks: [
        h3('Dados da conta e do uso do sistema'),
        ul(
          'Nome, e-mail e senha de quem acessa o sistema (a senha é guardada apenas em forma de hash, nunca em texto legível).',
          `Dados da ${N}: nome, unidades, endereços, horários de funcionamento, equipe e permissões.`,
          'Dados de assinatura: plano, situação (teste, ativa, em atraso, cancelada) e histórico de mudanças.',
          'Dados de segurança e auditoria: sessões abertas (data, aparelho/navegador e endereço IP), registro das operações críticas (quem fez, o quê e quando) e o aceite dos Termos e desta Política (versão e data).',
        ),
        h3(`Dados que a ${N} cadastra sobre seus clientes`),
        ul(
          'Cadastro do cliente: nome, telefone, e-mail, CPF, data de nascimento, preferências, observações, etiquetas e profissional preferido, conforme o que a barbearia decidir preencher.',
          'Agendamentos e histórico de atendimentos, pagamentos, caixa, comissões, pontos de fidelidade, planos, pacotes e cupons.',
          'Consentimento para receber comunicações de marketing por e-mail (com data) e a opção de não receber mensagens por WhatsApp.',
          'Dados que o próprio cliente informa no agendamento online, em avaliações e em mensagens de contato do site da barbearia.',
        ),
        h3('Dados sensíveis'),
        p(`O ${P} não tem campos próprios para dados de saúde ou outros dados pessoais sensíveis. Os campos de texto livre (por exemplo, preferências e observações do cliente) são preenchidos pela ${N}: recomendamos não registrar ali informações de saúde, religião, origem racial ou outros dados sensíveis. Se a ${N} optar por registrá-los, ela é a responsável pela base legal correspondente.`),
        h3('Mensagens ao assistente virtual'),
        p('Quando o recurso de inteligência artificial é usado, o conteúdo da pergunta e as informações necessárias para respondê-la são enviados ao provedor de IA (seção 6). Em regra, o assistente do site público conversa com visitantes, que não devem informar dados pessoais nele.'),
      ],
    },
    {
      id: 'finalidades',
      title: '3. Para que usamos os dados e com que base legal',
      blocks: [
        ul(
          'Criar e manter a conta, autenticar o acesso e prestar o serviço contratado (execução de contrato, LGPD art. 7º, V).',
          'Cobrar a assinatura e cumprir obrigações fiscais e contábeis (execução de contrato e obrigação legal, art. 7º, V e II).',
          'Enviar e-mails e avisos do sistema, como convites, recuperação de senha e lembretes de agendamento (execução de contrato).',
          'Manter a segurança, prevenir fraudes e abusos, registrar auditoria e investigar incidentes (legítimo interesse, art. 7º, IX, e cumprimento de dever legal).',
          'Dar suporte e melhorar o serviço com dados agregados de uso e de desempenho (legítimo interesse).',
          `Tratar, como operadora, os dados dos clientes da ${N} para as finalidades definidas por ela (a base legal é de responsabilidade da ${N}, que é a controladora).`,
        ),
        p('Não vendemos dados pessoais e não os usamos para publicidade de terceiros.'),
      ],
    },
    {
      id: 'compartilhamento',
      title: '4. Com quem compartilhamos (operadores e suboperadores)',
      blocks: [
        p('Para prestar o serviço, contratamos fornecedores que tratam dados em nosso nome. Compartilhamos apenas o necessário para cada função:'),
        ul(
          'Hospedagem e infraestrutura: provedor de infraestrutura contratado, onde rodam a aplicação, o banco de dados e as cópias de segurança.',
          'Armazenamento de arquivos: serviço de armazenamento de objetos compatível com S3, para imagens e arquivos enviados ao sistema e ao site da barbearia.',
          'E-mail transacional: Resend, para convites, recuperação de senha, lembretes e demais e-mails do sistema.',
          'Inteligência artificial: OpenAI ou Anthropic. Podemos utilizar um ou outro, conforme o que melhor atender aos requisitos de qualidade das respostas e de preço. O provedor pode mudar sem aviso individual, e esta lista será atualizada quando isso ocorrer.',
          `WhatsApp: as mensagens de agendamento (confirmação, lembrete e cancelamento) saem pelo número de WhatsApp que a própria ${N} conecta ao sistema. O WhatsApp (Meta) é um operador autônomo dessa troca de mensagens, com seus próprios termos e política de privacidade. Usamos um motor de integração (Evolution API) instalado em nossa infraestrutura para fazer essa conexão.`,
          'Cobrança: Asaas, quando a cobrança da assinatura estiver ativada, para processar pagamentos. Não armazenamos dados de cartão; o pagamento é feito em ambiente hospedado pelo provedor.',
          'Fontes tipográficas: as telas carregam fontes do Google Fonts, o que expõe o endereço IP do visitante ao Google para essa finalidade.',
        ),
        p('Também podemos compartilhar dados quando a lei, uma ordem judicial ou uma autoridade competente exigir, e em caso de reorganização societária, com aviso aos titulares quando aplicável. Exigimos dos fornecedores deveres de confidencialidade e segurança compatíveis com a LGPD.'),
      ],
    },
    {
      id: 'ia',
      title: '5. Inteligência artificial',
      blocks: [
        ul(
          `Uso opcional e sob controle do plano: os recursos de IA (assistente de gestão, resumo do dia, sugestões de campanhas e assistente do site) só funcionam quando estão disponíveis no plano da ${N} e dentro do limite mensal dele.`,
          'Dados enviados: apenas o necessário para a função pedida. O assistente consulta o sistema por ferramentas de leitura com escopo restrito à organização e ao perfil de quem pergunta, e as instruções do assistente o proíbem de pedir ou exibir telefone, e-mail e CPF.',
          'Não há decisões automatizadas sem revisão humana: o assistente apenas propõe ações (por exemplo, um agendamento ou uma campanha) e uma pessoa precisa confirmá-las na tela. Ele não executa alterações sozinho.',
          'As respostas são sugestões e podem conter erros: devem ser conferidas pela pessoa responsável antes de qualquer decisão.',
          'Registro de uso: guardamos metadados de cada interação (organização, usuário, recurso usado, provedor, modelo, ferramentas acionadas e quantidade de tokens), sem armazenar o conteúdo das perguntas e respostas em nossos registros.',
          'O provedor de IA recebe o conteúdo da requisição para gerar a resposta, sob as regras do contrato com ele.',
        ),
      ],
    },
    {
      id: 'transferencia-internacional',
      title: '6. Transferência internacional de dados',
      blocks: [
        p('Alguns de nossos fornecedores, como os provedores de IA e de e-mail, podem processar dados fora do Brasil. Quando isso ocorre, a transferência observa o art. 33 da LGPD, com as salvaguardas contratuais oferecidas por esses fornecedores (como cláusulas de proteção de dados) e a adoção das medidas de segurança descritas nesta política.'),
      ],
    },
    {
      id: 'retencao',
      title: '7. Por quanto tempo guardamos e como excluímos',
      blocks: [
        ul(
          'Mantemos os dados enquanto a conta estiver ativa e durante o prazo necessário para cumprir obrigações legais, resolver disputas e fazer valer nossos direitos.',
          `Cancelada a assinatura, o acesso continua até o fim do período já pago e a organização fica somente leitura. Os dados são mantidos por um período de retenção definido na plataforma e, depois dele, a exclusão ocorre em duas etapas, com uma janela em que ainda pode ser cancelada, antes da remoção definitiva.`,
          `A ${N} pode exportar seus dados a qualquer momento, inclusive com a conta suspensa ou cancelada.`,
          'Algumas exclusões no sistema são lógicas (o registro deixa de aparecer, mas permanece por um tempo para manter a integridade do histórico, como o financeiro). Quando uma conta de usuário é removida, ela é anonimizada.',
          'Cópias de segurança do banco são mantidas por um período limitado e depois descartadas; dados excluídos podem permanecer nelas até o fim desse prazo.',
        ),
      ],
    },
    {
      id: 'direitos',
      title: '8. Seus direitos como titular',
      blocks: [
        p('Nos termos do art. 18 da LGPD, você pode solicitar: confirmação de que tratamos seus dados; acesso; correção de dados incompletos, inexatos ou desatualizados; anonimização, bloqueio ou eliminação de dados desnecessários ou tratados em desconformidade; portabilidade; informação sobre compartilhamento; eliminação dos dados tratados com consentimento; informação sobre a possibilidade de não consentir e suas consequências; e revogação do consentimento.'),
        p(`Para exercer esses direitos, escreva para ${MAIL}. Podemos pedir informações para confirmar sua identidade. Respondemos no prazo previsto em lei.`),
        p(`Se você é cliente de uma ${N} que usa o ${P}, seus dados foram cadastrados por ela, que é a controladora. Nesse caso, fale primeiro com a ${N}. Se você nos procurar, encaminharemos o pedido a ela ou a apoiaremos para atendê-lo, como operadora.`),
        p('Você também pode apresentar reclamação à Autoridade Nacional de Proteção de Dados (ANPD).'),
      ],
    },
    {
      id: 'seguranca',
      title: '9. Segurança',
      blocks: [
        ul(
          'Criptografia em trânsito (HTTPS) nas conexões com o sistema.',
          'Senhas guardadas com hash forte (argon2id); recuperação de senha por link de uso único e com validade.',
          'Sessão por cookies marcados como httpOnly, com renovação rotativa e encerramento da sessão em caso de reuso suspeito do token; proteção contra requisições forjadas (CSRF) nas operações de escrita.',
          'Isolamento entre organizações: cada consulta é filtrada pela organização do usuário, e os identificadores recebidos do navegador são sempre conferidos contra ela.',
          'Permissões por papel (proprietário, gerente, recepcionista, barbeiro e papéis personalizados).',
          'Registro de auditoria das operações críticas e limitação de tentativas em rotas sensíveis.',
          'Acesso de suporte da plataforma à conta de um cliente (“entrar como”) só com perfil autorizado, por tempo limitado e com registro.',
        ),
        p('Nenhum sistema é totalmente imune a falhas. Por isso, também pedimos que cada ' + N + ' proteja suas senhas e dê acesso apenas a quem precisa.'),
      ],
    },
    {
      id: 'incidentes',
      title: '10. Incidentes de segurança',
      blocks: [
        p(`Se ocorrer um incidente de segurança que possa acarretar risco ou dano relevante aos titulares, comunicaremos a ${N} afetada (controladora) e, quando exigido pela LGPD, a ANPD e os titulares, informando a natureza do incidente, os dados envolvidos e as medidas adotadas.`),
      ],
    },
    {
      id: 'criancas',
      title: '11. Crianças e adolescentes',
      blocks: [
        p(`O ${P} é destinado a profissionais e empresas. A ${N} pode cadastrar clientes menores de idade (por exemplo, cortes infantis); nesse caso, é responsável por tratar esses dados no melhor interesse da criança ou do adolescente e, para crianças, com o consentimento específico de ao menos um dos pais ou responsável legal. Não oferecemos contas de acesso a menores de 18 anos.`),
      ],
    },
    {
      id: 'cookies',
      title: '12. Cookies e armazenamento local',
      blocks: [
        ul(
          'Cookies essenciais de sessão: autenticação (acesso e renovação, ambos httpOnly) e proteção contra CSRF. Sem eles o sistema não funciona e, por isso, não dependem de consentimento.',
          'Armazenamento local do navegador: guardamos apenas a preferência de tema (claro, escuro ou do sistema).',
          'Não usamos cookies de publicidade nem rastreadores de terceiros para análise de comportamento.',
        ),
      ],
    },
    {
      id: 'alteracoes',
      title: '13. Alterações desta política',
      blocks: [
        p('Podemos atualizar esta política. Cada versão tem um identificador e uma data, exibidos no topo desta página. Em mudanças relevantes, pediremos um novo aceite ao entrar no sistema.'),
      ],
    },
    {
      id: 'contato',
      title: '14. Contato',
      blocks: [
        p(`Dúvidas, solicitações e reclamações sobre privacidade: ${MAIL}.`),
        ...(COMPANY.cnpj || COMPANY.address
          ? [p([`${C}`, COMPANY.cnpj ? `CNPJ ${COMPANY.cnpj}` : '', COMPANY.address ?? ''].filter(Boolean).join(' · '))]
          : []),
      ],
    },
  ],
};

export const TERMS: LegalDoc = {
  title: 'Termos de Uso',
  intro: `Estes Termos regem o uso do ${P}, serviço de gestão para barbearias (agenda, clientes, caixa, fidelidade, site com agendamento online e assistente de IA), oferecido pela ${C}. Ao criar uma conta ou aceitar um convite, você declara que leu e concorda com estes Termos e com a Política de Privacidade.`,
  sections: [
    {
      id: 'servico',
      title: '1. O serviço e os planos',
      blocks: [
        p(`O ${P} é um software como serviço, acessado pela internet. As funções disponíveis, os limites (como número de usuários, unidades, agendamentos por mês, armazenamento e requisições de IA) e o preço dependem do plano contratado, exibido na tela de assinatura. Podemos alterar, incluir ou remover funcionalidades, avisando com antecedência razoável quando a mudança prejudicar o uso contratado.`),
      ],
    },
    {
      id: 'conta',
      title: '2. Conta e responsabilidades da barbearia',
      blocks: [
        ul(
          'Quem cria a conta declara ter poderes para representar a barbearia e fornecer dados verdadeiros e atualizados.',
          `A ${N} é responsável por sua equipe: quem recebe acesso, com quais permissões, e pelo sigilo das senhas. Atos realizados com as credenciais da conta presumem-se feitos por ela.`,
          `A ${N} é a controladora dos dados de seus clientes. Ela deve ter base legal para cadastrá-los e usá-los (inclusive o consentimento do cliente para mensagens de marketing, quando for o caso), informar os clientes sobre o tratamento e atender aos pedidos deles.`,
          `As mensagens de WhatsApp saem pelo número que a própria ${N} conecta. Ela responde pelo conteúdo e pelo uso desse número, deve respeitar a opção de não receber mensagens e os termos do WhatsApp, e está ciente de que o serviço usa uma integração não oficial, de modo que o número pode ser limitado ou bloqueado pelo WhatsApp.`,
          'Cada usuário deve aceitar estes Termos e a Política de Privacidade para usar o sistema; a versão e a data do aceite são registradas.',
        ),
      ],
    },
    {
      id: 'uso-aceitavel',
      title: '3. Uso aceitável',
      blocks: [
        p('É proibido:'),
        ul(
          'usar o sistema para fins ilícitos, fraudulentos ou que violem direitos de terceiros;',
          'enviar spam, mensagens em massa não solicitadas ou conteúdo ofensivo, discriminatório ou enganoso;',
          'tentar acessar dados de outras organizações, burlar limites do plano, testar a segurança sem autorização ou sobrecarregar a infraestrutura;',
          'copiar, revender, descompilar ou fazer engenharia reversa do software, salvo quando a lei permitir;',
          'compartilhar credenciais de forma que contorne o limite de usuários do plano.',
        ),
      ],
    },
    {
      id: 'assinatura',
      title: '4. Assinatura, teste, cobrança e cancelamento',
      blocks: [
        ul(
          'O plano pode incluir um período de teste, conforme informado no momento da contratação. Ao final dele, é preciso ter uma assinatura ativa para continuar usando o sistema com todas as funções.',
          'A cobrança é recorrente, no valor e na periodicidade do plano. O pagamento é processado por provedor de cobrança, em ambiente hospedado por ele; não armazenamos dados de cartão.',
          'Em caso de atraso, a conta passa por uma fase de aviso e carência e pode ser suspensa. Suspensa, a organização fica somente leitura: é possível entrar, consultar e exportar dados, mas não registrar novas operações até a regularização.',
          'Você pode cancelar a assinatura pela tela de assinatura. O acesso continua até o fim do período já pago. Não há reembolso proporcional do período em curso, salvo quando a lei assegurar esse direito.',
          'Podemos reajustar preços, com aviso prévio razoável antes de valerem para o próximo ciclo.',
        ),
      ],
    },
    {
      id: 'ia-email',
      title: '5. Recursos de IA e e-mail incluídos na assinatura',
      blocks: [
        ul(
          'Os recursos de inteligência artificial e o envio de e-mails do sistema fazem parte da assinatura, dentro dos limites do plano (por exemplo, o número de requisições de IA por mês). Não é preciso contratar nem configurar esses serviços à parte.',
          'Para prestar esses recursos, usamos fornecedores terceiros, como OpenAI ou Anthropic (IA) e Resend (e-mail), conforme a Política de Privacidade. Podemos trocar de fornecedor sem prejuízo da funcionalidade.',
          'A IA é um apoio: pode errar, inventar ou omitir informações, e não substitui o julgamento da pessoa responsável. Não garantimos resultado nem exatidão das respostas; confira antes de agir, sobretudo em decisões financeiras e de comunicação com clientes.',
          'Os recursos podem ficar indisponíveis quando o limite do plano for atingido ou quando o serviço de um fornecedor estiver fora do ar. Não garantimos a entrega de e-mails, que depende também dos provedores dos destinatários.',
        ),
      ],
    },
    {
      id: 'disponibilidade',
      title: '6. Disponibilidade e suporte',
      blocks: [
        p('Trabalhamos para manter o sistema disponível, mas não prometemos funcionamento ininterrupto: pode haver manutenção, falhas e indisponibilidade de fornecedores. Não oferecemos, por estes Termos, um índice numérico de disponibilidade. O suporte é prestado por chamados dentro do sistema ou pelo e-mail de contato, em horário comercial e sem prazo garantido de resposta.'),
      ],
    },
    {
      id: 'propriedade',
      title: '7. Propriedade intelectual',
      blocks: [
        p(`O software, a marca ${P}, o layout e a documentação pertencem à ${C} ou a seus licenciantes. Concedemos à ${N} uma licença limitada, revogável, não exclusiva e intransferível de uso durante a assinatura. O conteúdo que a ${N} envia (logos, fotos, textos, dados) continua sendo dela, e ela nos autoriza a hospedá-lo e processá-lo apenas para prestar o serviço.`),
      ],
    },
    {
      id: 'dados',
      title: '8. Dados da barbearia, exportação e exclusão',
      blocks: [
        ul(
          `Os dados cadastrados pela ${N} são dela. Nós os tratamos como operadora, conforme a Política de Privacidade.`,
          'Ela pode exportar seus dados a qualquer momento pela função de exportação do sistema, inclusive com a conta suspensa ou cancelada.',
          'Encerrada a assinatura, mantemos os dados por um período de retenção; depois dele, os dados podem ser excluídos em duas etapas, conforme a Política. Recomendamos exportar os dados antes do encerramento.',
        ),
      ],
    },
    {
      id: 'responsabilidade',
      title: '9. Limitação de responsabilidade',
      blocks: [
        p(`O serviço é fornecido “no estado em que se encontra”, nos limites da lei. A ${C} não responde por lucros cessantes, perda de agenda ou de clientes, ou danos indiretos decorrentes de indisponibilidade, de erros nas respostas da IA, de uso indevido da conta pela ${N} ou sua equipe, de bloqueio do número de WhatsApp, ou de falhas de fornecedores fora de nosso controle. Quando houver responsabilidade, ela se limita, na medida permitida em lei, ao valor pago pela ${N} nos 12 meses anteriores ao fato. Nada nestes Termos exclui responsabilidades que a lei não permita limitar, inclusive as previstas no Código de Defesa do Consumidor, quando aplicável.`),
      ],
    },
    {
      id: 'suspensao',
      title: '10. Suspensão e encerramento',
      blocks: [
        p(`Podemos suspender ou encerrar o acesso em caso de inadimplência, violação destes Termos, uso que cause risco à segurança ou a terceiros, ou determinação legal, avisando sempre que possível. A ${N} pode encerrar a conta a qualquer momento, cancelando a assinatura. As cláusulas que, por sua natureza, devam continuar valendo (propriedade intelectual, responsabilidade, foro) permanecem após o encerramento.`),
      ],
    },
    {
      id: 'alteracoes',
      title: '11. Alterações destes Termos',
      blocks: [
        p('Podemos atualizar estes Termos. Cada versão tem identificador e data. Em mudanças relevantes, pediremos novo aceite ao entrar no sistema; continuar usando após o aceite significa concordar com a nova versão.'),
      ],
    },
    {
      id: 'lei-foro',
      title: '12. Lei aplicável e foro',
      blocks: [
        p(`Estes Termos são regidos pelas leis do Brasil. Fica eleito o foro da comarca de ${forumText(COMPANY.forum)}, ressalvado o foro do domicílio do consumidor quando a lei assim determinar.`),
        p(`Contato: ${COMPANY.contactEmail}.`),
        ...(COMPANY.cnpj || COMPANY.address
          ? [p([`${C}`, COMPANY.cnpj ? `CNPJ ${COMPANY.cnpj}` : '', COMPANY.address ?? ''].filter(Boolean).join(' · '))]
          : []),
      ],
    },
  ],
};
